import { useEffect, useState, useCallback } from 'react';

/**
 * PWA install hook. Handles:
 *  - Chromium `beforeinstallprompt` capture + prompt()
 *  - Detects already-installed (standalone display-mode or iOS navigator.standalone)
 *  - Detects iOS (where no beforeinstallprompt fires — returns canInstall=true + iosHint flag)
 *
 *  Returns: { canInstall, installed, iosHint, install(): Promise<'accepted'|'dismissed'|'ios'|'unsupported'> }
 */
export function usePwaInstall() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const [chromiumReady, setChromiumReady] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator.standalone;
    if (isStandalone) { setInstalled(true); return; }

    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    if (isIOS) setIosHint(true);

    const onBip = (e) => { e.preventDefault(); setDeferred(e); setChromiumReady(true); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); setChromiumReady(false); };
    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (installed) return 'installed';
    if (iosHint) return 'ios';
    if (!deferred) return 'unsupported';
    try {
      deferred.prompt();
      const r = await deferred.userChoice;
      setDeferred(null); setChromiumReady(false);
      if (r?.outcome === 'accepted') { setInstalled(true); return 'accepted'; }
      return 'dismissed';
    } catch { return 'dismissed'; }
  }, [deferred, iosHint, installed]);

  const canInstall = !installed && (chromiumReady || iosHint);
  return { canInstall, installed, iosHint, install };
}
